class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.isAnimating = false;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onKeydown = this.onKeydown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.isClosing = false;
  }

  connectedCallback() {
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('click', this.onClick);
    this.setHeaderCartIconAccessibility();
    this.observeContentUpdates();
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('click', this.onClick);
    this.contentObserver?.disconnect();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.drawerReady === 'true') return;

    cartLink.dataset.drawerReady = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains('active')) {
      event.preventDefault();
      this.close();
    }
  }

  async onClick(event) {
    if (event.target.closest('.drawer__close, #CartDrawer-Overlay')) {
      this.close();
      return;
    }

    const quantityButton = event.target.closest('.quantity__button');
    if (quantityButton) this.animateQuantityControl(quantityButton);

    const addButton = event.target.closest('[data-recommendation-add]');
    if (!addButton || addButton.disabled) return;

    const label = addButton.querySelector('span');
    const originalLabel = label?.textContent || 'Add to bag';
    addButton.disabled = true;
    addButton.classList.add('is-loading');
    if (label) label.textContent = 'Adding';

    try {
      const result = await window.rflxnsCartService?.addVariant(
        { id: addButton.dataset.variantId, quantity: 1 },
        { openDrawer: true, showSignal: false }
      );
      if (!result?.ok) throw new Error(result?.data?.description || 'Unable to add this item.');
    } catch (error) {
      const errorRegion = this.querySelector('#CartDrawer-CartErrors');
      if (errorRegion) errorRegion.textContent = error.message;
      addButton.disabled = false;
      addButton.classList.remove('is-loading');
      if (label) label.textContent = originalLabel;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    if (this.classList.contains('active') && !this.isClosing) {
      this.animateUpdatedContent();
      const refreshedPanel = this.querySelector('.drawer__inner');
      const refreshedFocusTarget = this.querySelector('.drawer__close') || refreshedPanel;
      if (refreshedPanel && refreshedFocusTarget) {
        if (typeof trapFocus === 'function') trapFocus(refreshedPanel, refreshedFocusTarget);
        else refreshedFocusTarget.focus();
      }
      return;
    }

    this.isClosing = false;
    const panel = this.querySelector('.drawer__inner');
    const overlay = this.querySelector('.cart-drawer__overlay');
    if (!panel || !overlay) return;

    this.isAnimating = true;
    this.classList.add('active');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    window.rflxnsLenis?.stop();
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');

    const finishOpen = () => {
      this.isAnimating = false;
      this.motionTimeline = null;
      if (window.gsap) window.gsap.set(panel, { clearProps: 'transform' });
      const focusTarget = this.querySelector('.drawer__close') || panel;
      if (typeof trapFocus === 'function') trapFocus(panel, focusTarget);
      else focusTarget.focus();
    };

    if (this.reduceMotion.matches || typeof window.gsap === 'undefined') {
      panel.style.transform = 'translateX(0)';
      overlay.style.opacity = '1';
      finishOpen();
      return;
    }

    window.gsap.killTweensOf([panel, overlay]);
    this.motionTimeline?.kill();
    this.motionTimeline = window.gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: finishOpen });
    this.motionTimeline
      .fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.38 }, 0)
      .fromTo(panel, { x: panel.offsetWidth }, { x: 0, duration: 0.68 }, 0)
      .fromTo(
        this.querySelectorAll('[data-cart-animate], [data-cart-line]'),
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.055, clearProps: 'opacity,transform' },
        0.22
      );
    this.animateProgress(this.motionTimeline, 0.32);
  }

  close() {
    if (!this.classList.contains('active') || this.isClosing) return;
    this.isClosing = true;

    const panel = this.querySelector('.drawer__inner');
    const overlay = this.querySelector('.cart-drawer__overlay');
    const finishClose = () => {
      this.isClosing = false;
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      window.rflxnsLenis?.start();
      document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      else this.activeElement?.focus();
      this.isAnimating = false;
      this.motionTimeline = null;
      if (window.gsap) window.gsap.set([panel, overlay], { clearProps: 'all' });
    };

    this.isAnimating = true;
    if (this.reduceMotion.matches || typeof window.gsap === 'undefined') {
      finishClose();
      return;
    }

    this.motionTimeline?.kill();
    window.gsap.killTweensOf([panel, overlay]);
    this.motionTimeline = window.gsap.timeline({ onComplete: finishClose })
      .to(panel, { x: panel.offsetWidth, duration: 0.48, ease: 'power3.in' }, 0)
      .to(overlay, { opacity: 0, duration: 0.35, ease: 'power2.in' }, 0.08);
  }

  animateQuantityControl(button) {
    // Clean, immediate response without transform scaling
  }

  animateProgress(timeline, position = 0) {
    const fill = this.querySelector('.rflxns-shipping__fill');
    if (!fill || this.reduceMotion.matches || typeof window.gsap === 'undefined') return;
    const targetWidth = getComputedStyle(fill).getPropertyValue('--shipping-progress').trim();
    timeline.fromTo(fill, { width: 0 }, { width: targetWidth, duration: 0.85, ease: 'power3.out' }, position);
  }

  animateUpdatedContent() {
    // Content update handled cleanly via CSS
  }

  observeContentUpdates() {
    // No layout shift mutation observer
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    const innerCartDrawer = document.getElementById('CartDrawer');
    if (innerCartDrawer) innerCartDrawer.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!target || !parsedState.sections?.[section.id]) return;
      target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    if (innerCartDrawer) innerCartDrawer.classList.remove('is-empty');
    requestAnimationFrame(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : parsed.body.innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
